import React, { useEffect } from 'react';
import { Box, Typography } from "@material-ui/core";
import { connect } from "react-redux";
import { ApplicationState } from "../../redux/reducers";
import { ICliente } from "../../entities/catalogos/ICliente";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { setGuiaOrigen, setGuiaWizard } from "../../redux/actions/guiaActions";
import useGetClienteDistribuidorInHouse, {
    IClienteDistribuidorInHouse
} from "../../hooks/useGetClienteDistribuidorInHouse";
import useCommonApi from "../../api/useCommonApi";
import { AreaServicio } from "../../entities/catalogos/AreaServicio";
import { Sucursal } from "../../entities/catalogos/Sucursal";
import { Colonia } from "../../entities/catalogos/Colonia";
import { initialRecoleccionState } from "../../redux/reducers/recoleccionReducer";
import { getDireccionDistribuidor } from "../../blocks/utility";
import { traducirLabel } from "../../blocks/traductor";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { enums } from "../../enums/enums";
import LoadingDialog from "../controls/LoadingDialog";
import RecoleccionFechaGuia from "./RecoleccionFechaGuia";
import {
    calcularAceptaCitaRadOrigen,
    prepararAreaServicioOrigenGuia
} from "./guiaRecoleccionOrigenHelpers";
import { origenGuiaCompleto } from "./guiaServiciosOrigenDestinoHelpers";

interface StateProps {
    cliente: ICliente;
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
}

interface DispatchProps {
    setGuiaOrigen: typeof setGuiaOrigen;
    setGuiaWizard: typeof setGuiaWizard;
}

type Props = StateProps & DispatchProps;

type DistribuidorInHouseConTelefono = IClienteDistribuidorInHouse & {
    telefono?: string | null;
};

const tieneCampoTelefono = (
    distribuidor: IClienteDistribuidorInHouse
): distribuidor is DistribuidorInHouseConTelefono =>
    Object.prototype.hasOwnProperty.call(distribuidor, "telefono");

const telefonoDistribuidorInHouse = (distribuidor: IClienteDistribuidorInHouse): string => {
    if (!tieneCampoTelefono(distribuidor)) {
        return "";
    }
    const valor = distribuidor.telefono;
    return typeof valor === "string" ? valor.trim() : "";
};

const telefonoVisible = (contacto: { telefono?: string } | null | undefined): string => {
    if (!contacto || typeof contacto.telefono !== "string") {
        return "";
    }
    return contacto.telefono.trim();
};

const OrigenInHouseGuia = ({
    cliente,
    guiaWizard,
    guiaOrigen,
    guiaDestino,
    setGuiaOrigen,
    setGuiaWizard
}: Props) => {
    const { obtenerInHousePorCliente } = useGetClienteDistribuidorInHouse();
    const apiSource = useCommonApi();
    const [loading, setLoading] = React.useState(false);

    useEffect(() => {
        if (!cliente?.id) {
            return;
        }
        if (origenGuiaCompleto(guiaWizard, guiaOrigen)) {
            return;
        }
        cargarOrigenInHouse();
    }, [cliente?.id]);

    const mensajeSinInHouse = traducirLabel(359);

    const mensajeMultiplesInHouse = localStorage.getItem("idioma") === 'en-US'
        ? 'More than one InHouse origin address was found. Contact your administrator before creating a guide.'
        : 'Se encontró más de una dirección de origen InHouse. Contacte a su administrador antes de generar la guía.';

    const mensajeSinCobertura = localStorage.getItem("idioma") === 'en-US'
        ? 'The InHouse origin address has no service coverage. A collection branch could not be resolved.'
        : 'La dirección InHouse no tiene cobertura de servicio. No fue posible resolver la sucursal de recolección.';

    const mensajeErrorApi = traducirLabel(361);

    const cargarOrigenInHouse = async () => {
        setLoading(true);
        setGuiaWizard({
            ...guiaWizard,
            inHouseStatus: 'loading',
            inHouseMensaje: '',
            remitenteId: 0,
            sucursalRemitenteId: 0
        });
        setGuiaOrigen({ ...initialRecoleccionState });

        try {
            const registros = await obtenerInHousePorCliente(cliente.id);

            if (!registros || registros.length === 0) {
                setGuiaWizard({
                    ...guiaWizard,
                    inHouseStatus: 'none',
                    inHouseMensaje: mensajeSinInHouse,
                    remitenteId: 0,
                    sucursalRemitenteId: 0
                });
                setLoading(false);
                return;
            }

            if (registros.length > 1) {
                setGuiaWizard({
                    ...guiaWizard,
                    inHouseStatus: 'multiple',
                    inHouseMensaje: mensajeMultiplesInHouse,
                    remitenteId: 0,
                    sucursalRemitenteId: 0
                });
                setLoading(false);
                return;
            }

            const inHouse = registros[0];
            const telefono = telefonoDistribuidorInHouse(inHouse);
            const contacto = telefono ? { telefono } : undefined;
            const colonia = inHouse.colonium ? new Colonia(inHouse.colonium) : undefined;
            const direccion = inHouse.direccion || getDireccionDistribuidor(inHouse);

            const params = {
                paisId: colonia?.pais?.id || inHouse.paisID,
                estadoId: colonia?.estado?.id || inHouse.estadoID,
                ciudadId: colonia?.ciudad?.id || inHouse.ciudadID,
                codigoPostal: colonia?.codigoPostal || inHouse.codigoPostal,
                empresaId: cliente.empresaID,
                ClienteID: cliente.id,
                ClienteDistribuidorID: inHouse.clienteDistribuidorID
            };

            const result: AreaServicio = await apiSource.obtenerAsync("area-servicio/by-address-cliente-distribuidor", params);
            const areaServicio = result ? new AreaServicio(result) : undefined;
            let sucursal: Sucursal | undefined;
            if (areaServicio?.sucursal?.id) {
                try {
                    sucursal = new Sucursal(areaServicio.sucursal);
                } catch {
                    sucursal = areaServicio.sucursal;
                }
            }
            const sucursalRemitenteId = sucursal?.id || 0;

            if (!areaServicio?.id || !sucursalRemitenteId) {
                setGuiaOrigen({
                    ...initialRecoleccionState,
                    paisId: inHouse.paisID || 1,
                    codigoPostal: inHouse.codigoPostal || '',
                    direccion,
                    colonia,
                    distribuidor: inHouse,
                    areaServicio,
                    sucursal,
                    sinAreaServicio: true,
                    completado: false,
                    contacto
                });
                setGuiaWizard({
                    ...guiaWizard,
                    inHouseStatus: 'sinCobertura',
                    inHouseMensaje: mensajeSinCobertura,
                    remitenteId: inHouse.clienteDistribuidorID,
                    sucursalRemitenteId: 0
                });
                setLoading(false);
                return;
            }

            const areaPreparada = prepararAreaServicioOrigenGuia(areaServicio);
            const esServicioInternacional = (inHouse.paisID || 1) === enums.pais.usa;
            setGuiaOrigen({
                ...initialRecoleccionState,
                paisId: inHouse.paisID || 1,
                codigoPostal: inHouse.codigoPostal || '',
                direccion,
                colonia,
                distribuidor: {
                    ...inHouse,
                    clienteDistribuidorID: inHouse.clienteDistribuidorID,
                    nombre: inHouse.nombreClienteDistribuidor
                },
                areaServicio: areaPreparada,
                sucursal,
                sinAreaServicio: false,
                completado: false,
                tipoRecoleccionId: enums.tipoRecoleccion.comercial,
                aceptaCita: calcularAceptaCitaRadOrigen(cliente, areaPreparada, esServicioInternacional),
                esServicioInternacional,
                serviciosAdicionales: [...(areaServicio.serviciosAdicionales ?? [])] as ServicioAdicional[],
                contacto
            });
            setGuiaWizard({
                ...guiaWizard,
                inHouseStatus: 'ok',
                inHouseMensaje: '',
                remitenteId: inHouse.clienteDistribuidorID,
                sucursalRemitenteId
            });
        } catch {
            setGuiaWizard({
                ...guiaWizard,
                inHouseStatus: 'error',
                inHouseMensaje: mensajeErrorApi,
                remitenteId: 0,
                sucursalRemitenteId: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const origenBloqueado = guiaWizard.inHouseStatus !== 'ok';
    const telefonoContacto = telefonoVisible(guiaOrigen.contacto);

    return (<>
        {loading && <LoadingDialog titulo={'Procesando'} abierto={true} />}
        <Box className="card" p={2} mb={2} style={{ backgroundColor: "#f7f7f7" }}>
            {guiaOrigen.distribuidor &&
                <>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        {guiaOrigen.distribuidor.nombre || guiaOrigen.distribuidor.nombreClienteDistribuidor}
                    </Typography>
                    <Typography variant="body2">{guiaOrigen.direccion}</Typography>
                    {(guiaOrigen.areaServicio?.id ?? 0) > 0 &&
                        <Typography variant="body2" style={{ marginTop: 8 }}>
                            {traducirLabel(222)}: {guiaOrigen.sucursal?.nombre || ''}
                        </Typography>
                    }
                    {telefonoContacto &&
                        <Typography variant="body2" style={{ marginTop: 8 }}>
                            {traducirLabel(292).toUpperCase()}: {telefonoContacto}
                        </Typography>
                    }
                </>
            }
            {origenBloqueado && guiaWizard.inHouseMensaje &&
                <Typography color="error" style={{ marginTop: 8 }}>
                    {guiaWizard.inHouseMensaje}
                </Typography>
            }
            {!origenBloqueado &&
                <Typography variant="caption" color="textSecondary">
                    {traducirLabel(360)}
                </Typography>
            }
        </Box>
        {!origenBloqueado && (guiaOrigen.areaServicio?.id ?? 0) > 0 &&
            <RecoleccionFechaGuia
                guiaOrigen={guiaOrigen}
                guiaDestino={guiaDestino}
                cliente={cliente}
                onChange={setGuiaOrigen}
            />
        }
    </>);
};

const mapStateToProps = (state: ApplicationState) => ({
    cliente: state.cliente,
    guiaWizard: state.guiaWizard,
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino
});

const mapDispatchToProps = {
    setGuiaOrigen,
    setGuiaWizard
};

export default connect(mapStateToProps, mapDispatchToProps)(OrigenInHouseGuia);
